import type { IconType, RowProps } from '@viewup/ui';
import { ArrowRight, Button, Column, Icon, Info, Row, ToggleSwitch, Typography } from '@viewup/ui';
import type { ReactElement, ReactNode } from 'react';
import React from 'react';
import type { PlatformCategoryCategoryEnum } from 'viewup-api';
import { exchangeSnsIcon } from '../../constants';

interface AccountStatusProps extends RowProps {
  account: {
    hasError: boolean;
    snsType: PlatformCategoryCategoryEnum | 'viewupPlus';
    userName: string;
  };
  children: ReactNode;
  styledComponents?: {
    info?: RowProps;
    root?: RowProps;
    top?: RowProps;
  };
}
export const AccountStatus = (props: AccountStatusProps) => {
  const { children, styledComponents, account } = props;

  const childArray = React.Children.toArray(children) as ReactElement[];

  const rightContent =
    childArray.find((child) => child.type === AccountStatus.RightContent) || null;
  const bottomContent =
    childArray.find((child) => child.type === AccountStatus.BottomContent) || null;

  return (
    <Column
      border='1 solid border.tertiary'
      br={16}
      gap={8}
      px={16}
      py={12}
      {...styledComponents?.root}
    >
      <Row justifyContent='space-between' {...styledComponents?.top}>
        <Row alignItems='center' gap={8} {...styledComponents?.info}>
          <Icon size='S' source={exchangeSnsIcon(account.snsType)} />
          <Typography color={account.hasError ? 'text.disabled' : 'text.primary'} ellipsisLine={1}>
            {account.userName}
          </Typography>
        </Row>
        {rightContent}
      </Row>
      {bottomContent}
    </Column>
  );
};

interface AccountStatusRightContentProps extends RowProps {
  children: ReactNode;
}
AccountStatus.RightContent = function AccountStatusRightContent({
  children,
  ...props
}: AccountStatusRightContentProps) {
  return (
    <Row alignItems='center' gap={8} {...props}>
      {children}
    </Row>
  );
};

interface AccountStatusBottomContentProps extends RowProps {
  children: ReactNode;
}
AccountStatus.BottomContent = function AccountStatusBottomContent({
  children,
  ...props
}: AccountStatusBottomContentProps) {
  return (
    <Column br={8} gap={8} justifyContent='space-between' mt={4} width='100%' {...props}>
      {children}
    </Column>
  );
};

interface AccountStatusActiveBoxProps extends RowProps {
  checked: boolean;
  onValueChange?: (checked: boolean) => void;
  text: string;
}
AccountStatus.ActiveBox = function ActiveBox({
  text,
  checked,
  onValueChange,
  ...props
}: AccountStatusActiveBoxProps) {
  const { onChange } = props;
  return (
    <Row
      alignItems='center'
      bgColor='bg.tertiary'
      br={8}
      justifyContent='space-between'
      px={12}
      py={8}
      width='100%'
      {...props}
    >
      <Typography>{text}</Typography>
      <ToggleSwitch
        checked={checked}
        onChange={(e) => (onValueChange ? onValueChange(e.currentTarget.checked) : onChange)}
      />
    </Row>
  );
};

interface AccountStatusErrorBoxProps extends RowProps {
  buttonLabel?: string;
  buttonLabelHandler?: () => void;
  icon?: IconType;
  text: string;
}
AccountStatus.ErrorBox = function ErrorBox({
  icon = Info,
  text,
  buttonLabel,
  buttonLabelHandler,
  ...props
}: AccountStatusErrorBoxProps) {
  return (
    <Row
      alignItems='center'
      bgColor='bg.dangerSubtle'
      br={8}
      color='icon.danger'
      css={{
        cursor: !buttonLabel && 'pointer',
      }}
      height={40}
      justifyContent='space-between'
      pl={16}
      pr={12}
      py={8}
      width='100%'
      {...props}
    >
      <Row alignItems='center' gap={4}>
        {icon && <Icon size='3XS' source={icon} />}
        <Typography fontType='caption2.regular'>{text}</Typography>
      </Row>
      {buttonLabel && buttonLabelHandler ? (
        <Button
          onClick={buttonLabelHandler}
          rightContent={<Icon size='3XS' source={ArrowRight} />}
          size='XS'
          styledComponents={{
            root: {
              color: 'text.danger',
              gap: 4,
            },
          }}
          variant='ghost.assistive'
        >
          {buttonLabel}
        </Button>
      ) : (
        <Icon size='3XS' source={ArrowRight} />
      )}
    </Row>
  );
};
